/**
 * Vehicle identification numbers.
 *
 * Only the parts of a VIN that mean the same thing for every carmaker are
 * decoded: the first character gives the region, and the first three (the
 * world manufacturer identifier, WMI) the maker. Everything after that is laid
 * out differently by each maker, and in Europe the model-year position is not
 * even required, so it is not interpreted.
 */

export interface VinInfo {
  vin: string;
  /** World manufacturer identifier: the first three characters. */
  wmi: string;
  /** Carmaker, when the WMI is a well-known one. */
  manufacturer?: string;
  /** Where the car was built, as far as the first characters say. */
  region: string;
}

/**
 * Tidy a VIN as read from a car or a file, or return undefined if it is not
 * one. ECUs that were never programmed report all zeros or all spaces.
 */
export function normalizeVin(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const vin = v.trim().toUpperCase();
  // 17 characters since 1981; I, O and Q are never used. Some older cars
  // report a shorter chassis number.
  if (!/^[A-HJ-NPR-Z0-9]{11,17}$/.test(vin)) return undefined;
  if (/^(.)\1+$/.test(vin)) return undefined;
  return vin;
}

export function decodeVin(v: string): VinInfo | undefined {
  const vin = normalizeVin(v);
  if (!vin) return undefined;
  const wmi = vin.slice(0, 3);
  const info: VinInfo = { vin, wmi, region: regionOf(vin) };
  const manufacturer = MANUFACTURERS[wmi];
  if (manufacturer) info.manufacturer = manufacturer;
  return info;
}

/** "…A12345": enough to tell two cars apart without printing the whole VIN. */
export function shortVin(vin: string): string {
  return '…' + vin.slice(-6);
}

/** ISO 3779 regions, with the countries that are unambiguous from two characters. */
function regionOf(vin: string): string {
  const a = vin[0] ?? '';
  const ab = vin.slice(0, 2);
  const within = (lo: string, hi: string): boolean => ab >= lo && ab <= hi;

  if (a === 'J') return 'Japan';
  if (within('KL', 'KR')) return 'South Korea';
  if (a === 'L') return 'China';
  if (within('MA', 'ME')) return 'India';
  if (within('SA', 'SM')) return 'United Kingdom';
  if (within('TJ', 'TP')) return 'Czech Republic';
  if (within('TR', 'TV')) return 'Hungary';
  if (ab === 'UU') return 'Romania';
  if (within('VA', 'VE')) return 'Austria';
  if (within('VF', 'VR')) return 'France';
  if (within('VS', 'VW')) return 'Spain';
  if (a === 'W') return 'Germany';
  if (within('YS', 'YW')) return 'Sweden';
  if (a === 'Z') return 'Italy';
  if (a === '1' || a === '4' || a === '5') return 'United States';
  if (a === '2') return 'Canada';
  if (a === '3') return 'Mexico';
  if (a === '9') return 'Brazil';
  if (a === '6') return 'Australia';

  if (a >= 'A' && a <= 'H') return 'Africa';
  if (a >= 'J' && a <= 'R') return 'Asia';
  if (a >= 'S' && a <= 'Z') return 'Europe';
  if (a >= '1' && a <= '5') return 'North America';
  if (a === '6' || a === '7') return 'Oceania';
  if (a === '8' || a === '9') return 'South America';
  return 'Unknown region';
}

/** Well-known world manufacturer identifiers. Not exhaustive; unknown ones get the region only. */
const MANUFACTURERS: Record<string, string> = {
  // France, Spain, Romania
  VF1: 'Renault', VF3: 'Peugeot', VF7: 'Citroën', VR3: 'Peugeot', VR7: 'Citroën',
  VNK: 'Toyota', UU1: 'Dacia', VSS: 'SEAT', VS6: 'Ford',
  // Germany
  WVW: 'Volkswagen', WV1: 'Volkswagen Commercial Vehicles', WV2: 'Volkswagen Commercial Vehicles',
  WAU: 'Audi', WBA: 'BMW', WBS: 'BMW M', WMW: 'MINI', WDB: 'Mercedes-Benz', WDD: 'Mercedes-Benz',
  WDC: 'Mercedes-Benz', W1K: 'Mercedes-Benz', WME: 'smart', WP0: 'Porsche', WP1: 'Porsche',
  W0L: 'Opel/Vauxhall', W0V: 'Opel/Vauxhall', WF0: 'Ford',
  // Central Europe
  TMB: 'Škoda', TRU: 'Audi', TMA: 'Hyundai', TSM: 'Suzuki', U5Y: 'Kia',
  // Italy
  ZFA: 'Fiat', ZAR: 'Alfa Romeo', ZLA: 'Lancia', ZFF: 'Ferrari', ZAM: 'Maserati', ZHW: 'Lamborghini',
  // Sweden, UK, Turkey
  YV1: 'Volvo', YS3: 'Saab', SAL: 'Land Rover', SAJ: 'Jaguar', SCC: 'Lotus', SJN: 'Nissan',
  SB1: 'Toyota', SHH: 'Honda', NMT: 'Toyota', NM0: 'Ford',
  // Japan, Korea, India, China
  JTD: 'Toyota', JTE: 'Toyota', JT2: 'Toyota', JTH: 'Lexus', JHM: 'Honda', JN1: 'Nissan',
  JMZ: 'Mazda', JM1: 'Mazda', JF1: 'Subaru', JS2: 'Suzuki', JMB: 'Mitsubishi', JA3: 'Mitsubishi',
  KMH: 'Hyundai', KNA: 'Kia', KND: 'Kia', MA3: 'Suzuki', MAL: 'Hyundai',
  LSV: 'Volkswagen', LFV: 'Volkswagen', LRW: 'Tesla',
  // Americas
  '1FA': 'Ford', '1FT': 'Ford', '1G1': 'Chevrolet', '1GC': 'Chevrolet', '1HG': 'Honda', '1N4': 'Nissan',
  '2HG': 'Honda', '3VW': 'Volkswagen', '4T1': 'Toyota', '5N1': 'Nissan', '5YJ': 'Tesla', '7SA': 'Tesla',
  '9BW': 'Volkswagen',
};
